import { on } from '@ember/modifier';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { action, computed } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import { didInsert, didUpdate } from '@ember/render-modifiers';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { getCssVariableValue } from 'frontend-utils/utils/css-variable';
import { MI_DEFAULT_DATE_FORMAT } from 'lumen/utils/constants/general-constants';
import {
  FIXED_EXTRACT_DATE_LATEST,
  LEVEL_TO_DEMAND_COLOR_MAPPING,
} from 'lumen/utils/constants/market-insight-constants';

interface HotelMarketInsightDemandEvolutionSignature {
  Args: {
    currentExtractDate: unknown;
    extractDateIndex: unknown;
    isDetailRequestLoading: unknown;
    model: unknown;
    nbOfAvailableExtractDates: unknown;
    selectedDayModel: unknown;
    updateExtractDateIndex: unknown;
  };
}

export default class HotelMarketInsightDemandEvolution extends Component<HotelMarketInsightDemandEvolutionSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  executeSliderUpdateTask = task(
    { restartable: true },
    async (updatedIndex) => {
      // @ts-expect-error: Incorrect type
      await this.args.updateExtractDateIndex(
        // @ts-expect-error: Incorrect type
        this.args.nbOfAvailableExtractDates - 1 - updatedIndex,
      );
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get evolutionPattern() {
    // @ts-expect-error: Incorrect type
    const patternArray = [];

    if (
      !this.isEvolutionSliderDisabled &&
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.selectedDayModel?.summaries.length
    ) {
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const summaries = [...this.args.selectedDayModel.summaries].filter(
        (summary) => {
          // The number of slider steps are determined by the geodata.meta.extract_dates in the detail request. We should generate the same amount of blocks in the pattern here
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const summaryDate = summary.id.split(',')[1];
          if (
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            !this.args.model.map.data.geoData.meta.extract_dates.includes(
              summaryDate,
            )
          ) {
            return false;
          }

          // Filter out summaries that won't have any reliable information
          // Extra note: re-added this check on 28/07 after we've removed it 3 months earlier. Having it more loose can result in generating to many blocks in this component (mostly for days further in the future or close to max allowed date)
          // The test link from the moment when we've loosened the check doesn't work anymore, so we'll see if something pops up again in the future.
          if (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.isSummaryMissing ||
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            summary.isDestinationDemandIndexMissing
          ) {
            return false;
          }

          // Check if the demandLevelColorScaleNumber exists. If this doesn't exist we can't show anything anyway
          // This property of the summary model already has a fallback to the destination demand index if the demand index data is missing
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (!summary.demandLevelColorScaleNumber) {
            return false;
          }

          return true;
        },
      );

      summaries.sort((a, b) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (a.isLatest) {
          return 1;
        }
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (b.isLatest) {
          return -1;
        }

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        return a.extractDate.unix() - b.extractDate.unix();
      });

      const halfOfTickWidth = 1; // Ticks are 2% wide
      const numOfTicks = summaries.length;
      const hasEvenNumOfTicks = numOfTicks % 2 === 0;
      const numOfTicksWithOffset = hasEvenNumOfTicks
        ? numOfTicks
        : numOfTicks - 1; // Middle tick has no offset
      const halfOfTicksWithOffset = numOfTicksWithOffset / 2;

      summaries.forEach((summary, index) => {
        const sign = index < halfOfTicksWithOffset ? 1 : -1;
        // The factor is the shortest distance to either the start or the end of the array (depending on the current index)
        const factor = Math.min(index, numOfTicksWithOffset - index - 1);

        let offset = 0;
        // If there is an even number of ticks, the center tick position does not need to be corrected
        if (hasEvenNumOfTicks || index !== halfOfTicksWithOffset) {
          // We correct the position of the ticks so they are evenly spaced on the range input
          offset =
            sign *
            (halfOfTicksWithOffset - factor) *
            (halfOfTickWidth / numOfTicksWithOffset);
        }

        patternArray.push({
          // Ticks are 2% wide (-1 for start, +1 for end)
          // We also make sure the tick is capped between 0 and 100 (see Math.max and Math.min)
          start: Math.max(
            (index / (summaries.length - 1)) * 100 +
              2 * offset -
              halfOfTickWidth,
            0,
          ),
          end: Math.min(
            (index / (summaries.length - 1)) * 100 +
              2 * offset +
              halfOfTickWidth,
            100,
          ),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          color:
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            LEVEL_TO_DEMAND_COLOR_MAPPING[summary.demandLevelColorScaleNumber],
        });
      });
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return patternArray;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedExtractDate() {
    if (this.args.currentExtractDate) {
      if (this.args.currentExtractDate === FIXED_EXTRACT_DATE_LATEST) {
        return this.intl.t('market_insight.extract_date.now');
      }

      return this.intl.t('market_insight.extract_date.as_seen_on', {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        date: this.args.currentExtractDate.format(MI_DEFAULT_DATE_FORMAT),
      });
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isEvolutionSliderDisabled() {
    return (
      this.args.isDetailRequestLoading ||
      this.args.nbOfAvailableExtractDates === 1 ||
      this.args.nbOfAvailableExtractDates === null
    );
  }

  @computed('args.model.{calendar.isLoading,detail.isLoading}')
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isLoadingSummariesOrDetail() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.args.model?.calendar?.isLoading || this.args.model?.detail?.isLoading
    );
  }

  // Set the maxValue for the paper slider. This should equal the number of extract dates returned by the API, but 0-based because we use an array
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sliderMaxValue() {
    // @ts-expect-error: Incorrect type
    return this.args.nbOfAvailableExtractDates > 1
      ? // @ts-expect-error: Incorrect type
        this.args.nbOfAvailableExtractDates - 1
      : 1;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get sliderValue() {
    if (this.isLoadingSummariesOrDetail) {
      // Keep the thumb at the far right of the track while loading
      return this.sliderMaxValue;
    }

    // @ts-expect-error: Incorrect type
    return this.args.nbOfAvailableExtractDates - 1 - this.args.extractDateIndex;
  }

  @action
  initSlider(): void {
    // @ts-expect-error: Incorrect type
    const gradients = [];

    this.evolutionPattern.forEach((pattern, index) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      gradients.push(`${pattern.color} ${pattern.start}% ${pattern.end}%`);

      // Add white filler between the ticks (otherwise the gradient kicks in)
      if (index + 1 !== this.evolutionPattern.length) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const nextTick = this.evolutionPattern[index + 1];
        gradients.push(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          `${getCssVariableValue('--prism-color-border-inverse-emphasis')} ${pattern.end}% ${nextTick.start}%`,
        );
      }
    });

    // @ts-expect-error: Incorrect type
    const linearGradientString = gradients.join(', ');
    const backgroundString = `linear-gradient(to right, ${linearGradientString})`;
    // @ts-expect-error: Incorrect type
    document.getElementById('evolution-slider').style.background =
      backgroundString;
  }

  // This is the onChange action for the evolution paper slider
  // Note: We directly update the paper slider value here, but the performance-heavy part is handled in a separate task (executeSliderUpdate)
  // This allows the UI to feel snappy, but to not execute the demanding code too much
  @action
  // @ts-expect-error: Incorrect type
  updateSliderValue(event): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const updatedIndex = event.srcElement.valueAsNumber;
    if (updatedIndex !== this.sliderValue) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.executeSliderUpdateTask.perform(updatedIndex);

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const day = this.args.selectedDayModel.date;
      // @ts-expect-error: Incorrect type
      const { outliers } = this.args.selectedDayModel;

      this.usageAnalytics.track('Demand Evolution Viewed', {
        eventTrigger: 'day-detail-map',
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
        daysOffsetFromToday: day
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .startOf('day')
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          .diff(
            this.hotelAndMarketService.getLocalTime().startOf('day'),
            'days',
          ),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        isOutlier: outliers.length > 0,
      });
    }
  }

  <template>
    <div
      class="city-map-overlay-item--demand-evolution__header flex flex-row items-center justify-between"
    >
      <p class="card-title text-300--bold">{{t
          "market_insight.demand_evolution"
        }}</p>
      <p
        class="evolution-extract-date text-200--regular"
        data-test-id="evolution-extract-date"
      >
        {{#if this.formattedExtractDate}}
          {{this.formattedExtractDate}}
        {{else}}
          {{t "general.loading"}}...
        {{/if}}
      </p>
    </div>

    <input
      data-test-id="evolution-slider"
      {{! @glint-expect-error: Incorrect type }}
      disabled={{this.isEvolutionSliderDisabled}}
      id="evolution-slider"
      max={{this.sliderMaxValue}}
      min={{0}}
      step={{1}}
      type="range"
      value={{this.sliderValue}}
      {{didInsert this.initSlider}}
      {{didUpdate
        this.initSlider
        @selectedDayModel
        this.isEvolutionSliderDisabled
      }}
      {{on "change" this.updateSliderValue}}
    />
  </template>
}
